import * as React from 'react';
import styled, { StyledComponent } from 'styled-components';
import WrappedMapBase from 'google-maps-wrapper';
import { Vehicle } from '../../VehiclesHOC';
import math_helpers from '../../helpers/math_helpers';

import RTBusRoute1 from '../../markers/rt-bus-route-1.svg';
import RTBusRoute2 from '../../markers/rt-bus-route-2.svg';
import RTBusRoute3 from '../../markers/rt-bus-route-3.svg';
import RTBusRoute4 from '../../markers/rt-bus-route-4.svg';
import RTBusRoute5 from '../../markers/rt-bus-route-5.svg';
import RTBusRoute6 from '../../markers/rt-bus-route-6.svg';
import RTBusRoute7 from '../../markers/rt-bus-route-7.svg';
import RTBusRoute11_13 from '../../markers/rt-bus-route-11-13.svg';
import RTBusRouteDefault from '../../markers/rt-bus-route-default.svg';

declare global {
    interface Window {
        google: any;
    }
}

interface BusesOverlayProps {
    map_ref: WrappedMapBase;
}

interface BusesOverlayState {}

//const DEFAULT_TRANSITION_LENGTH = 5; //seconds
const ICON_SIZE = 100; //Pixels.

const ICONS = {
    1: RTBusRoute1,
    2: RTBusRoute2,
    3: RTBusRoute3,
    4: RTBusRoute4,
    5: RTBusRoute5,
    6: RTBusRoute6,
    7: RTBusRoute7,
    11: RTBusRoute11_13,
    12: RTBusRoute11_13,
    13: RTBusRoute11_13,
    default: RTBusRouteDefault
};

class BusesOverlay extends React.Component<BusesOverlayProps, BusesOverlayState> {
    container: StyledComponent<"div", any>;
    container_ref: HTMLDivElement | null = null;
    buses: {
        [key: string]: {
            updated_at: number;
            element: HTMLDivElement;
            text_element: HTMLDivElement;
            angle: number;
        }
    } = {};

    constructor(props: BusesOverlayProps) {
        super(props);

        this.container = styled.div `
            position: fixed;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
        `;
    }

    shouldComponentUpdate() {
        return false;
    }

    updateBus(bus: Vehicle, transition = true) {
        if (!this.container_ref || !this.props.map_ref || !this.props.map_ref.initialized) { return; }
        let now = new Date().valueOf();
        let pos = this.props.map_ref.toPixel({ lat: bus.position.y, lng: bus.position.x });

        if (pos[0] < -ICON_SIZE || pos[0] > window.innerWidth + ICON_SIZE) {
            if (this.buses.hasOwnProperty(bus.id)) {
                this.removeBus(bus);
            }
            return;
        }
        if (pos[1] < -ICON_SIZE || pos[1] > window.innerHeight + ICON_SIZE) {
            if (this.buses.hasOwnProperty(bus.id)) {
                this.removeBus(bus);
            }
            return;
        }

        if (this.buses.hasOwnProperty(bus.id) === false) {
            let bus_div = document.createElement('div');

            bus_div.className = "bus bus_" + bus.id;

            let route_short_name = "";
            if (bus.hasOwnProperty("service") && bus.service) {
                route_short_name = (bus.service.route_id || "").split("-")[0];
            }
            let icon_url = "url(" + ICONS.default + ")";
            if (ICONS.hasOwnProperty(route_short_name)) {
                icon_url = "url(" + ICONS[route_short_name] + ")";
            }
            let st = bus_div.style;

            st.width = ICON_SIZE + "px";
            st.height = ICON_SIZE + "px";
            st.pointerEvents = "all";
            st.backgroundImage = icon_url;
            st.position = "absolute";
            st.backgroundRepeat = "no-repeat";
            st.backgroundSize = ICON_SIZE + "px " + ICON_SIZE + "px";
            st.backgroundPosition = "center";
            this.container_ref.appendChild(bus_div);

            let text_element = document.createElement('div');
            text_element.innerHTML = route_short_name;

            let text_st = text_element.style;
            text_st.transform = "rotate(-90deg)";
            text_st.position = "absolute";
            text_st.top = "0";
            text_st.left = "0";
            text_st.width = ICON_SIZE + "px";
            text_st.height = ICON_SIZE * 1.15 + "px";
            text_st.display = "flex";
            text_st.alignItems = "center";
            text_st.justifyContent = "center";
            text_st.fontFamily = 'helvetica, sens-serif';
            text_st.fontSize = Math.floor(ICON_SIZE * 0.22) + "px";
            text_st.color = 'rgba(0,0,0,0.7)';
            bus_div.appendChild(text_element);

            let angle = 0;
            if (bus.hasOwnProperty("velocity") && bus.velocity !== undefined && bus.velocity.hasOwnProperty("bearing") && bus.velocity.bearing !== null) {
                angle = bus.velocity.bearing || 0;
                if (angle < 0) {
                    text_st.transform = "rotate(90deg)";
                } else {
                    text_st.transform = "rotate(-90deg)";
                }
                angle = math_helpers.getNewAngle(0, angle);
                st.transform = "rotate(" + angle + "deg)";
            }


            this.buses[bus.id] = {
                updated_at: now,
                element: bus_div,
                text_element: text_element,
                angle: angle
            };
        }

        let drawn_bus = this.buses[bus.id];
        let updated_at = drawn_bus.updated_at;
        let st = this.buses[bus.id].element.style;
        if (updated_at !== now && transition) {
            //We assume next update will take as long as the previous and use that as transition time.
            // st.transition = "left " + ((now - updated_at) / 1000) + "s, top " + ((now - updated_at) / 1000) + "s" ;
            st.transition = "all " + ((now - updated_at) / 1000) + "s linear";
        } else if (transition === false) {
            st.transition = "";
        }
        st.left = (pos[0] - (ICON_SIZE / 2)) + "px";
        st.top = (pos[1] - (ICON_SIZE / 2)) + "px";
        if (bus.hasOwnProperty("velocity") && bus.velocity !== undefined && bus.velocity.hasOwnProperty("bearing") && bus.velocity.bearing !== null) {
            let angle = bus.velocity.bearing || 0;
            if (angle < 0) {
                drawn_bus.text_element.style.transform = "rotate(90deg)";
            } else {
                drawn_bus.text_element.style.transform = "rotate(-90deg)";
            }
            angle = math_helpers.getNewAngle(drawn_bus.angle, angle);
            st.transform = "rotate(" + angle + "deg)";

            drawn_bus.angle = angle;
        }

        drawn_bus.updated_at = now;
    }

    removeBus(bus: Vehicle) {
        if (!this.container_ref) { return; }
        if (this.buses.hasOwnProperty(bus.id) === false) {
            return;
        }
        this.container_ref.removeChild(this.buses[bus.id].element);
        delete this.buses[bus.id];
    }

    render() {
        return (
            <this.container>
                <div
                    ref={container_ref => this.container_ref = container_ref}
                />
            </this.container>
        );
    }
}

export default BusesOverlay;



// WEBPACK FOOTER //
// ./src/components/base/BusesOverlay.tsx